const chapter = {
  id: 'em-waves',
  slug: 'em-waves',
  subject: 'physics',
  subjectLabel: 'Physics',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Electromagnetic Waves',
        hindi: 'विद्युत चुम्बकीय तरंगें',
  },
  estReadMinutes: 35,
  difficulty: {
    english: 'Easy',
        hindi: 'आसान',
  },
  weightage: {
    english: '1 question in JEE Main / NEET',
        hindi: 'जेईई मेन/एनईईटी में 1 प्रश्न',
  },
  syllabusTags: ['Optics & Modern', 'JEE-Main', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'displacement-current',
      title: {
        english: 'Displacement Current & Maxwell\'s Equations',
        hindi: 'विस्थापन धारा और मैक्सवेल के समीकरण',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'Maxwell noted Ampère\'s law $\\oint \\vec B\\cdot d\\vec\\ell = \\mu_0 I$ fails for a charging capacitor (no conduction current between plates). He added a **displacement current** $I_d = \\varepsilon_0 \\,d\\Phi_E/dt$ proportional to changing electric flux.',
        hindi: 'मैक्सवेल ने कहा कि एम्पीयर का नियम $\\oint \\vec B\\cdot d\\vec\\ell = \\mu_0 I$ चार्जिंग कैपेसिटर (प्लेटों के बीच कोई चालन धारा नहीं) के लिए विफल रहता है। उन्होंने बदलते विद्युत प्रवाह के अनुपात में एक **विस्थापन धारा** $I_d = \\varepsilon_0 \\,d\\Phi_E/dt$ जोड़ा।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Modified Ampère–Maxwell law',
        hindi: 'संशोधित एम्पीयर-मैक्सवेल कानून',
          },
          statement: {
            english: '$\\displaystyle\\oint \\vec B \\cdot d\\vec\\ell = \\mu_0 (I_c + I_d) = \\mu_0 I_c + \\mu_0 \\varepsilon_0 \\dfrac{d\\Phi_E}{dt}.$',
        hindi: '$\\displaystyle\\oint \\vec B \\cdot d\\vec\\ell = \\mu_0 (I_c + I_d) = \\mu_0 I_c + \\mu_0 \\varepsilon_0 \\dfrac{d\\Phi_E}{dt}.$',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Maxwell\'s 4 equations** (integral form, vacuum):',
        hindi: '**मैक्सवेल के 4 समीकरण** (अभिन्न रूप, निर्वात):',
            },
            {
              english: '1. $\\oint \\vec E\\cdot d\\vec A = q/\\varepsilon_0$ (Gauss, electric).',
        hindi: '1. $\\oint \\vec E\\cdot d\\vec A = q/\\varepsilon_0$ (गॉस, इलेक्ट्रिक)।',
            },
            {
              english: '2. $\\oint \\vec B\\cdot d\\vec A = 0$ (Gauss, magnetic — no monopoles).',
        hindi: '2. $\\oint \\vec B\\cdot d\\vec A = 0$ (गॉस, चुंबकीय - कोई मोनोपोल नहीं)।',
            },
            {
              english: '3. $\\oint \\vec E\\cdot d\\vec\\ell = -d\\Phi_B/dt$ (Faraday).',
        hindi: '3. $\\oint \\vec E\\cdot d\\vec\\ell = -d\\Phi_B/dt$ (फैराडे)।',
            },
            {
              english: '4. $\\oint \\vec B\\cdot d\\vec\\ell = \\mu_0 I + \\mu_0\\varepsilon_0 \\,d\\Phi_E/dt$ (Ampère–Maxwell).',
        hindi: '4. $\\oint \\vec B\\cdot d\\vec\\ell = \\mu_0 I + \\mu_0\\varepsilon_0 \\,d\\Phi_E/dt$ (एम्पीयर-मैक्सवेल)।',
            },
          ],
        },
      ],
    },
    {
      id: 'em-wave-properties',
      title: {
        english: 'Properties of EM Waves',
        hindi: 'EM तरंगों के गुणधर्म',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'EM waves are **transverse**: $\\vec E, \\vec B, \\hat k$ form right-handed orthogonal triad. $\\vec E \\perp \\vec B$.',
        hindi: 'EM तरंगें **अनुप्रस्थ** होती हैं: $\\vec E, \\vec B, \\hat k$ दाएं हाथ के ऑर्थोगोनल ट्रायड का निर्माण करती हैं। $\\vec E \\perp \\vec B$.',
            },
            {
              english: 'Speed in vacuum: $c = 1/\\sqrt{\\mu_0\\varepsilon_0} = 3\\times 10^8$ m/s. In medium: $v = 1/\\sqrt{\\mu\\varepsilon} = c/n$.',
        hindi: 'निर्वात में गति: $c = 1/\\sqrt{\\mu_0\\varepsilon_0} = 3\\times 10^8$ मी/से. माध्यम में: $v = 1/\\sqrt{\\mu\\varepsilon} = c/n$.',
            },
            {
              english: 'Ratio of magnitudes: $E_0/B_0 = c$ (so $B_0$ is much smaller numerically).',
        hindi: 'परिमाण का अनुपात: $E_0/B_0 = c$ (इसलिए $B_0$ संख्यात्मक रूप से बहुत छोटा है)।',
            },
            {
              english: 'Energy density: $u = \\tfrac12 \\varepsilon_0 E^2 + B^2/(2\\mu_0)$; **electric and magnetic contributions equal**.',
        hindi: 'ऊर्जा घनत्व: $u = \\tfrac12 \\varepsilon_0 E^2 + B^2/(2\\mu_0)$; **विद्युत और चुंबकीय योगदान बराबर**।',
            },
            {
              english: '**Intensity** (time-averaged): $I = \\tfrac12 \\varepsilon_0 c E_0^2 = c\\,u_{\\text{avg}}$.',
        hindi: '**तीव्रता** (समय-औसत): $I = \\tfrac12 \\varepsilon_0 c E_0^2 = c\\,u_{\\text{avg}}$।',
            },
            {
              english: '**Momentum** carried by wave: $p = U/c$. **Radiation pressure**: $P_r = I/c$ (absorber), $2I/c$ (perfect reflector).',
        hindi: '**गति** तरंग द्वारा संचालित: $p = U/c$। **विकिरण दबाव**: $P_r = I/c$ (अवशोषक), $2I/c$ (परफेक्ट रिफ्लेक्टर)।',
            },
            {
              english: 'EM waves do **not** require a medium and are **not** deflected by electric or magnetic fields (they carry no charge).',
        hindi: 'EM तरंगों को किसी माध्यम की **नहीं** आवश्यकता होती है और ये विद्युत या चुंबकीय क्षेत्र द्वारा विक्षेपित **नहीं** होती हैं (इनमें कोई आवेश नहीं होता)।',
            },
          ],
        },
      ],
    },
    {
      id: 'spectrum',
      title: {
        english: 'Electromagnetic Spectrum',
        hindi: 'विद्युत चुम्बकीय स्पेक्ट्रम',
      },
      blocks: [
        {
          type: 'table',
          headers: ['Region', 'Wavelength', 'Source / Detector', 'Use'],
          rows: [
            ['Radio', '$> 0.1$ m', 'LC oscillators / receivers', 'Communication, AM/FM, TV'],
            ['Microwave', '$10^{-3}$–$10^{-1}$ m', 'Klystron, magnetron / point-contact diode', 'Radar, microwave oven'],
            ['Infrared (IR)', '$700$ nm–$1$ mm', 'Hot bodies, vibrations / thermopile', 'Heating, remote sensing, night vision'],
            ['Visible', '$\\approx 400$–$700$ nm', 'Atomic transitions / eye, photocell', 'Vision'],
            ['Ultraviolet (UV)', '$1$–$400$ nm', 'High-temp arcs, sun / photocells', 'Sterilization, vitamin-D synthesis'],
            ['X-rays', '$0.01$–$1$ nm', 'Bombarding metal targets / photographic plate', 'Medical imaging, crystallography'],
            ['Gamma rays', '$< 0.01$ nm', 'Nuclear transitions / Geiger', 'Cancer therapy, sterilization'],
          ],
        },
        {
          type: 'tip',
          text: {
            english: 'Order of increasing frequency / energy: Radio < Micro < IR < Visible < UV < X-ray < $\\gamma$.',
        hindi: 'बढ़ती आवृत्ति/ऊर्जा का क्रम: रेडियो < माइक्रो < आईआर < दृश्यमान < यूवी < एक्स-रे < $\\gamma$।',
          },
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'Frequency of green light of wavelength 500 nm.',
        hindi: 'तरंग दैर्ध्य 500 एनएम की हरी रोशनी की आवृत्ति।',
          },
          solution: {
            english: '$f = c/\\lambda = 3{\\times}10^8/(5{\\times}10^{-7}) = 6{\\times}10^{14}$ Hz.',
        hindi: '$f = c/\\lambda = 3{\\times}10^8/(5{\\times}10^{-7}) = 6{\\times}10^{14}$ Hz.',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'I_d = \\varepsilon_0\\,d\\Phi_E/dt',
      desc: {
        english: 'Displacement current',
        hindi: 'विस्थापन धारा',
      },
    },
    {
      latex: 'c = 1/\\sqrt{\\mu_0\\varepsilon_0}',
      desc: {
        english: 'Speed of light',
        hindi: 'प्रकाश की गति',
      },
    },
    {
      latex: 'E_0/B_0 = c',
      desc: {
        english: 'Field ratio',
        hindi: 'फ़ील्ड अनुपात',
      },
    },
    {
      latex: 'I = \\tfrac12 \\varepsilon_0 c E_0^2',
      desc: {
        english: 'Intensity',
        hindi: 'तीव्रता',
      },
    },
    {
      latex: 'P_r = I/c\\ \\text{(absorber)},\\ 2I/c\\ \\text{(reflector)}',
      desc: {
        english: 'Radiation pressure',
        hindi: 'विकिरण दबाव',
      },
    },
    {
      latex: 'v = c/n',
      desc: {
        english: 'Speed in medium',
        hindi: 'मध्यम गति',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Spectrum',
      problem: {
        english: 'Which has highest frequency: IR, UV, X-ray, microwave?',
        hindi: 'किसकी आवृत्ति सबसे अधिक है: आईआर, यूवी, एक्स-रे, माइक्रोवेव?',
      },
      answer: {
        english: 'X-ray',
        hindi: 'एक्स-रे',
      },
      solution: {
        english: 'Order increasing $f$: micro < IR < UV < X-ray.',
        hindi: 'ऑर्डर बढ़ रहा है $f$: माइक्रो < आईआर < यूवी < एक्स-रे।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Field ratio',
      problem: {
        english: 'Peak electric field of EM wave is 60 V/m. Peak magnetic field?',
        hindi: 'EM तरंग का चरम विद्युत क्षेत्र 60 V/m है। चरम चुंबकीय क्षेत्र?',
      },
      answer: {
        english: '$2\\times 10^{-7}$ T',
        hindi: '$2\\times 10^{-7}$ टी',
      },
      solution: {
        english: '$B_0 = E_0/c = 60/(3\\times 10^8)$.',
        hindi: '$B_0 = E_0/c = 60/(3\\times 10^8)$.',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Radiation pressure',
      problem: {
        english: 'Light of intensity $6$ W/m² is fully absorbed. Radiation pressure?',
        hindi: '$6$ W/m² तीव्रता का प्रकाश पूरी तरह से अवशोषित होता है। विकिरण दबाव?',
      },
      answer: {
        english: '$2\\times 10^{-8}$ Pa',
        hindi: '$2\\times 10^{-8}$ पा',
      },
      solution: {
        english: '$P_r = I/c = 6/(3\\times 10^8)$.',
        hindi: '$P_r = I/c = 6/(3\\times 10^8)$।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Displacement current',
      problem: {
        english: 'In a parallel-plate capacitor being charged, current between the plates is by means of __',
        hindi: 'चार्ज किए जा रहे समानांतर-प्लेट कैपेसिटर में, प्लेटों के बीच करंट __ के माध्यम से होता है',
      },
      answer: {
        english: 'displacement current',
        hindi: 'विस्थापन धारा',
      },
      solution: {
        english: 'No conduction current in vacuum gap; $I_d$ ensures continuity of Ampère law.',
        hindi: 'वैक्यूम गैप में कोई चालन धारा नहीं; $I_d$ एम्पीयर कानून की निरंतरता सुनिश्चित करता है।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Spectrum',
      problem: {
        english: 'Wavelength range of visible light?',
        hindi: 'दृश्य प्रकाश की तरंग दैर्ध्य सीमा?',
      },
      answer: {
        english: '$400$–$700$ nm',
        hindi: '$400$–$700$ एनएम',
      },
      solution: {
        english: 'Standard.',
        hindi: 'मानक।',
      },
    },
  ],
  next: {
    slug: 'ray-optics',
    title: 'Ray Optics & Optical Instruments',
  },
};

export default chapter;
